/**
 * Texto del aviso de privacidad (T033; research R-7).
 *
 * El contenido vive en `privacy-notice.es-PE.md` y se embebe en el build con `?raw` (Vite), así que
 * no hay una petición extra ni dependencia de markdown: el archivo usa un subconjunto mínimo
 * (encabezados `#`/`##`, listas `-` y párrafos) que `parseNotice` convierte en bloques.
 *
 * La **versión vigente** no está en el texto: la manda la API (`privacy_notice.version` de
 * `ActivationInfo` / `StaffMe`) y la pantalla la muestra junto al aviso.
 */
import noticeMarkdown from './privacy-notice.es-PE.md?raw';

/** Markdown crudo del aviso (útil en pruebas). */
export const PRIVACY_NOTICE_MARKDOWN = noticeMarkdown;

export type NoticeBlock =
  | { kind: 'heading'; level: 1 | 2; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

/** Markdown mínimo → bloques. Sin dependencias: encabezados, listas con `-` y párrafos. */
export function parseNotice(markdown: string): NoticeBlock[] {
  const blocks: NoticeBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
  };
  const flushList = (): void => {
    if (list.length > 0) {
      blocks.push({ kind: 'list', items: list });
      list = [];
    }
  };

  for (const rawLine of markdown.split('\n')) {
    const line = rawLine.trim();

    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = /^(#{1,2})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length === 1 ? 1 : 2,
        text: heading[2]!.trim(),
      });
      continue;
    }

    const item = /^-\s+(.*)$/.exec(line);
    if (item) {
      flushParagraph();
      list.push(item[1]!.trim());
      continue;
    }

    // Continuación de un ítem de lista partido en varias líneas por el ancho del archivo.
    if (list.length > 0) {
      list[list.length - 1] = `${list[list.length - 1]!} ${line}`;
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
}

const NOTICE_BLOCKS = parseNotice(PRIVACY_NOTICE_MARKDOWN);

export interface PrivacyNoticeTextProps {
  /** Se omite el `#` del aviso cuando la pantalla ya lo usa como título. */
  hideTitle?: boolean;
  className?: string;
}

/** Aviso completo, desplazable dentro del contenido de la pantalla. */
export function PrivacyNoticeText({ hideTitle = true, className }: PrivacyNoticeTextProps) {
  const blocks = hideTitle
    ? NOTICE_BLOCKS.filter((block) => !(block.kind === 'heading' && block.level === 1))
    : NOTICE_BLOCKS;

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.kind === 'heading') {
          return block.level === 1 ? (
            <h2
              key={index}
              className="mt-fm-4 font-display text-fm-xl font-bold text-fm-text first:mt-0"
            >
              {block.text}
            </h2>
          ) : (
            <h3
              key={index}
              className="mt-fm-4 font-display text-fm-lg font-semibold text-fm-text first:mt-0"
            >
              {block.text}
            </h3>
          );
        }
        if (block.kind === 'list') {
          return (
            <ul key={index} className="mt-fm-2 flex list-disc flex-col gap-fm-2 pl-fm-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex} className="text-fm-base text-fm-text-secondary">
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={index} className="mt-fm-2 text-fm-base text-fm-text-secondary">
            {block.text}
          </p>
        );
      })}
    </div>
  );
}
